import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, ExternalLink, Info, Eye, EyeOff, EyeClosed, Flag, Undo2 } from 'lucide-react';
import {
  DEFAULT_PREFS, METHODS, COUNT_WINDOWS, DAY_WINDOWS, HOLD,
  priceFrom, salesForTier,
} from '../../utils/priceMath';
import {
  fetchHeldOut, reportSale, unreportSale, excludeSale, unexcludeSale,
  itemIdFromUrl, REASONS,
} from '../../utils/saleReports';
import { palette, COPY, money2, relativeAge } from './pricingTokens';
import haptics from '../../utils/haptics';
import BottomSheet from '../BottomSheet';

/**
 * Price deep dive. Every sale behind one number, with the maths live.
 *
 * The controls are chips sitting under the price, not a settings sheet behind
 * an icon. Every tap recomputes from the sales array already in memory, so the
 * price, the caption, the counted line and which rows carry the IN AVG marker
 * all move together with no network call and no spinner. That immediacy is the
 * feature; hiding it a tap deeper would waste it.
 *
 * Two separate ideas share the word flagged and must not be conflated:
 *
 *   COUNTING - whether flagged sales enter the calculation.
 *   VISIBILITY - whether they are drawn in the list.
 *
 * They are independent, with one guard rail: if flagged sales are being
 * counted, they are forced visible and the show/hide control disappears. A
 * sale that moves the average while invisible makes the average unexplainable.
 */

const PANEL = { NONE: null, INFO: 'info' };

const tierLabel = (tier) => String(tier || '')
  .replace(/_/g, ' ')
  .replace(/(\d) (\d)/, '$1.$2');

const QUALITY_WORD = {
  NEAR_MINT: 'Near mint',
  LIGHTLY_PLAYED: 'Lightly played',
  MODERATELY_PLAYED: 'Moderately played',
  HEAVILY_PLAYED: 'Heavily played',
  DAMAGED: 'Damaged',
};

/** Counts up to a new price rather than snapping. Under 300ms, per the spec. */
function useRollingNumber(target) {
  const [shown, setShown] = useState(target);
  const fromRef = useRef(target);
  const rafRef = useRef(null);

  useEffect(() => {
    const from = fromRef.current;
    if (target == null || from == null || from === target) {
      fromRef.current = target;
      setShown(target);
      return undefined;
    }
    // Someone who has asked for less motion gets the answer, not the animation.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
      fromRef.current = target;
      setShown(target);
      return undefined;
    }

    const started = performance.now();
    const DURATION = 260;
    const tick = (now) => {
      const t = Math.min(1, (now - started) / DURATION);
      const eased = 1 - (1 - t) ** 3;
      setShown(from + (target - from) * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
      else fromRef.current = target;
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target]);

  return shown;
}

// What the tag says when a sale is held out. Naming the reason matters here:
// a sale struck through with no explanation reads as the app losing data.
const HELD_LABEL = {
  [HOLD.REPORTED]:  'REPORTED',
  [HOLD.FLAGGED]:   'FLAGGED',
  [HOLD.SLAB]:      'GRADED',
  [HOLD.MISGRADED]: 'WRONG GRADE',
  [HOLD.EXCLUDED]:  'EXCLUDED',
};

export default function SaleBreakdownSheet({
  isOpen, onClose, card, evidence, tier, source = 'eBay sold',
  prefs, onSavePrefs, onHeldOutChange, theme, isDark = true,
}) {
  const [local, setLocal] = useState(prefs || DEFAULT_PREFS);
  const [held, setHeld] = useState({ mine: new Set(), consensus: new Set(), excluded: new Set() });
  // The row whose reason picker is open. One at a time, inline, so the sale
  // being judged stays on screen next to the reasons.
  const [reporting, setReporting] = useState(null);
  const [busy, setBusy] = useState(null);
  const [panel, setPanel] = useState(PANEL.NONE);
  const [showFlagged, setShowFlagged] = useState(false);

  useEffect(() => { if (isOpen) setLocal(prefs || DEFAULT_PREFS); }, [isOpen, prefs]);

  useEffect(() => {
    if (!isOpen || !card) return undefined;
    let alive = true;
    fetchHeldOut(card).then((r) => { if (alive) setHeld(r); });
    return () => { alive = false; };
  }, [isOpen, card]);

  const heldOut = useMemo(() => ({
    reported: new Set([...held.mine, ...held.consensus]),
    excluded: held.excluded || new Set(),
  }), [held]);

  // The ladder is still on screen behind this sheet, and the card's own price
  // is drawn from the same sales. Excluding a comp here and finding the number
  // underneath unchanged would read as the tap not having worked.
  const pushUp = useRef(onHeldOutChange);
  pushUp.current = onHeldOutChange;
  useEffect(() => { pushUp.current?.(heldOut); }, [heldOut]);

  const sales = useMemo(() => salesForTier(evidence, tier), [evidence, tier]);
  const result = useMemo(
    () => priceFrom(sales, local, heldOut),
    [sales, local, heldOut],
  );

  const flaggedCount = useMemo(
    () => result.rows.filter((r) => r.held).length,
    [result],
  );

  // Both actions move the set in state before the write lands, and put it back
  // if the write fails. The price recomputing on the same tap is the point of
  // this screen; waiting on a round trip to see it would waste that.
  const patchHeld = useCallback((field, id, add) => {
    setHeld((prev) => {
      const next = new Set(prev[field] || []);
      if (add) next.add(id); else next.delete(id);
      return { ...prev, [field]: next };
    });
  }, []);

  const toggleExclude = useCallback(async (sale) => {
    const id = itemIdFromUrl(sale?.url);
    if (!id || busy) return;
    const on = held.excluded?.has(id);

    setBusy(id);
    haptics.tap();
    patchHeld('excluded', id, !on);

    const res = on
      ? await unexcludeSale({ card, sale })
      : await excludeSale({ card, sale, tier });

    if (!res.ok) {
      patchHeld('excluded', id, on);
      haptics.warn();
    }
    setBusy(null);
  }, [busy, held.excluded, patchHeld, card, tier]);

  const submitReport = useCallback(async (sale, reason) => {
    const id = itemIdFromUrl(sale?.url);
    if (!id) return;

    setBusy(id);
    setReporting(null);
    haptics.tap();
    patchHeld('mine', id, true);

    // Reporting is also a statement that it should not be in your number, so
    // it excludes as well. Chase asked for "either or both"; both is what a
    // report already means.
    patchHeld('excluded', id, true);

    const res = await reportSale({
      card, sale, tier, reason, tierPrice: result.value,
    });

    if (!res.ok) {
      patchHeld('mine', id, false);
      patchHeld('excluded', id, false);
      haptics.warn();
    } else {
      haptics.success();
      excludeSale({ card, sale, tier }).catch(() => {});
    }
    setBusy(null);
  }, [patchHeld, card, tier, result.value]);

  const undoReport = useCallback(async (sale) => {
    const id = itemIdFromUrl(sale?.url);
    if (!id || busy) return;
    setBusy(id);
    haptics.tap();
    patchHeld('mine', id, false);
    patchHeld('excluded', id, false);
    const [a, b] = await Promise.all([
      unreportSale({ card, sale }),
      unexcludeSale({ card, sale }),
    ]);
    if (!a.ok && !b.ok) {
      patchHeld('mine', id, true);
      haptics.warn();
    }
    setBusy(null);
  }, [busy, patchHeld, card]);

  // The guard rail. Counting a sale nobody can see is not a setting, it is a
  // bug the user would have to take on faith.
  const forcedVisible = !local.holdOut;
  const flaggedVisible = forcedVisible || showFlagged;

  const rolled = useRollingNumber(result.value);

  // Every change writes through immediately. The spec asks for silent
  // persistence, so there is no save button anywhere in here.
  const change = useCallback((patch) => {
    haptics.select();
    setLocal((prev) => {
      const next = { ...prev, ...patch };
      onSavePrefs?.(next);
      return next;
    });
  }, [onSavePrefs]);

  const c = palette(theme, isDark);
  const s = styles(c);

  const visibleRows = result.rows.filter((r) => flaggedVisible || !r.held);
  const quality = QUALITY_WORD[tier] || tierLabel(tier);

  return (
    <BottomSheet open={isOpen} onClose={onClose} overlayStyle={s.overlay} sheetStyle={s.sheet} label="Sales behind this price">

        <div style={s.head}>
          <div style={{ minWidth: 0 }}>
            <div style={s.headTitle}>
              {card?.name}{card?.set_number ? ` · ${card.set_number}` : ''}
            </div>
            <div style={s.headSub}>
              {[card?.set_name, quality, source].filter(Boolean).join(' · ')}
            </div>
          </div>
          <button style={s.close} onClick={onClose} aria-label="Close">
            <X size={17} />
          </button>
        </div>

        <div style={s.body}>
          <div style={s.priceCard}>
            <div style={s.priceLabel}>Your price</div>
            <div style={s.priceHero}>
              {rolled == null ? '--' : money2(rolled)}
            </div>
            {/* Written from the same object that did the sum, never assembled
                by hand beside it. One of them would drift. */}
            <div style={s.priceCaption}>{result.basis}</div>
          </div>

          <div className="cc-chiprow" style={s.chipRow}>
            {COUNT_WINDOWS.map((n) => (
              <button
                key={`n${n}`}
                style={{ ...s.chip, ...(local.days == null && local.count === n ? s.chipOn : {}) }}
                onClick={() => change({ count: n, days: null })}
              >Last {n}</button>
            ))}
            {DAY_WINDOWS.map((w) => (
              <button
                key={`d${w.days}`}
                style={{ ...s.chip, ...(local.days === w.days ? s.chipOn : {}) }}
                onClick={() => change({ days: w.days })}
              >{w.label}</button>
            ))}
          </div>

          <div className="cc-chiprow" style={s.chipRow}>
            {METHODS.map((m) => (
              <button
                key={m.id}
                style={{ ...s.chip, ...(local.method === m.id ? s.chipOn : {}) }}
                onClick={() => change({ method: m.id })}
              >{m.label}</button>
            ))}
          </div>

          {flaggedCount > 0 && (
            <div style={s.segWrap}>
              <span style={s.segLabel}>Flagged sales</span>
              <div style={s.seg}>
                <button
                  style={{ ...s.segBtn, ...(!local.holdOut ? s.segBtnOn : {}) }}
                  onClick={() => change({ holdOut: false })}
                >Count them</button>
                <button
                  style={{ ...s.segBtn, ...(local.holdOut ? s.segBtnOn : {}) }}
                  onClick={() => change({ holdOut: true })}
                >Hold them out</button>
              </div>
            </div>
          )}

          <div style={s.toolbar}>
            <span style={s.toolbarCount}>
              {result.counted} of {sales.length} counted
              {flaggedCount > 0 ? ` · ${flaggedCount} flagged` : ''}
            </span>
            {flaggedCount > 0 && (
              <button
                style={s.infoBtn}
                onClick={() => { haptics.tap(); setPanel(panel === PANEL.INFO ? PANEL.NONE : PANEL.INFO); }}
                aria-label={COPY.flaggedTitle}
              ><Info size={14} /></button>
            )}
            {/* Only in hold-out mode. In count mode the rows are forced visible
                and a control that cannot be used is noise. */}
            {flaggedCount > 0 && local.holdOut && (
              <button
                style={s.eyeBtn}
                onClick={() => { haptics.tap(); setShowFlagged((v) => !v); }}
              >
                {showFlagged ? <EyeOff size={13} /> : <Eye size={13} />}
                {showFlagged ? 'Hide flagged' : `Show ${flaggedCount} flagged`}
              </button>
            )}
          </div>

          {/* Expands inline and pushes the list down. An overlay here would
              cover the very rows the definition is explaining. */}
          {panel === PANEL.INFO && (
            <div style={s.infoCard} onClick={() => setPanel(PANEL.NONE)}>
              <div style={s.infoTitle}>{COPY.flaggedTitle}</div>
              <div style={s.infoBody}>{COPY.flaggedBody}</div>
            </div>
          )}

          <div style={s.list}>
            {visibleRows.length === 0 && (
              <div style={s.empty}>{COPY.noSales}</div>
            )}
            {visibleRows.map(({ sale, held: heldWhy, counted }, i) => {
              const id = itemIdFromUrl(sale.url);
              const isExcluded = heldOut.excluded.has(id);
              const isMine = held.mine.has(id);
              const working = busy === id;

              return (
                <div
                  key={sale.url || i}
                  style={{
                    ...s.row,
                    ...(counted ? s.rowCounted : {}),
                  }}
                >
                  {/* The link is its own element rather than wrapping the row.
                      Buttons inside an anchor are not valid and every tap on
                      one would open eBay on the way through. */}
                  <a
                    style={{ ...s.rowLink, ...(heldWhy ? s.rowFlagged : {}) }}
                    href={sale.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => haptics.tap()}
                  >
                    <div style={s.rowTop}>
                      <span style={{ ...s.rowPrice, ...(heldWhy ? s.rowPriceStruck : {}) }}>
                        {money2(sale.price)}
                      </span>
                      <ExternalLink size={12} style={{ color: c.link, flex: 'none' }} />
                      {/* Both tags at once is a real state, not a conflict: in
                          count mode a flagged sale is genuinely in the average. */}
                      {counted && <span style={s.tagIn}>IN AVG</span>}
                      {heldWhy && (
                        <span style={s.tagFlag}>
                          {HELD_LABEL[heldWhy] || 'FLAGGED'}
                        </span>
                      )}
                      <span style={s.rowDate}>{relativeAge(sale.date)}</span>
                    </div>
                    <div style={s.rowTitle}>{sale.title}</div>
                  </a>

                  <div style={s.rowActions}>
                    <button
                      type="button"
                      style={{ ...s.act, ...(isExcluded ? s.actOn : {}) }}
                      onClick={() => toggleExclude(sale)}
                      disabled={working}
                      aria-pressed={isExcluded}
                    >
                      {isExcluded ? <Eye size={13} /> : <EyeClosed size={13} />}
                      {isExcluded ? 'Include' : 'Exclude'}
                    </button>

                    {isMine ? (
                      <button
                        type="button"
                        style={{ ...s.act, ...s.actOn }}
                        onClick={() => undoReport(sale)}
                        disabled={working}
                      >
                        <Undo2 size={13} />
                        Undo report
                      </button>
                    ) : (
                      <button
                        type="button"
                        style={s.act}
                        onClick={() => {
                          haptics.select();
                          setReporting(reporting === id ? null : id);
                        }}
                        disabled={working}
                      >
                        <Flag size={13} />
                        Report
                      </button>
                    )}
                  </div>

                  {/* Inline, under the sale it is about. A modal here would
                      cover the listing the reason is being chosen for. */}
                  {reporting === id && (
                    <div style={s.reasons}>
                      {REASONS.map((r) => (
                        <button
                          key={r.id}
                          type="button"
                          style={s.reason}
                          onClick={() => submitReport(sale, r.id)}
                        >
                          {r.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div style={s.footer}>{COPY.ebayDrop}</div>
        </div>
    </BottomSheet>
  );
}

const styles = (c) => ({
  overlay: {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.62)',
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 11000,
  },
  sheet: {
    position: 'relative', width: '100%', maxWidth: '520px',
    display: 'flex', flexDirection: 'column',
    background: c.sheet, borderTop: `0.5px solid ${c.hairline}`,
    borderRadius: '20px 20px 0 0', height: '92vh', overflow: 'hidden',
    paddingBottom: 'var(--safe-area-bottom, 0px)',
  },

  head: {
    display: 'flex', alignItems: 'flex-start', gap: '12px', flex: 'none',
    padding: '18px 16px 14px', borderBottom: `0.5px solid ${c.hairline}`,
  },
  headTitle: {
    fontSize: '17px', fontWeight: '500', color: c.white,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  headSub: {
    fontSize: '12px', color: c.secondary, marginTop: '3px',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  close: {
    flex: 'none', width: '34px', height: '34px', borderRadius: '10px', cursor: 'pointer',
    border: `0.5px solid ${c.chipBorder}`, background: c.input, color: c.primary,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },

  body: { flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '16px 16px 28px' },

  priceCard: {
    background: c.card, border: `0.5px solid ${c.hairline}`, borderRadius: '14px',
    padding: '16px', marginBottom: '14px',
  },
  priceLabel: {
    fontSize: '11px', fontWeight: '600', letterSpacing: '0.12em',
    textTransform: 'uppercase', color: c.muted,
  },
  priceHero: {
    fontSize: '38px', fontWeight: '500', color: c.white, letterSpacing: '-0.03em',
    lineHeight: 1.1, margin: '6px 0 4px', fontVariantNumeric: 'tabular-nums',
  },
  priceCaption: { fontSize: '12px', color: c.secondary, lineHeight: 1.45 },

  // Hidden scrollbar with a chip clipping at the edge, which is the only
  // affordance saying there is more to the right.
  chipRow: {
    display: 'flex', gap: '7px', overflowX: 'auto', marginBottom: '9px',
    scrollbarWidth: 'none', msOverflowStyle: 'none', paddingBottom: '1px',
  },
  chip: {
    flex: 'none', border: `0.5px solid ${c.chipBorder}`, background: 'transparent',
    color: c.secondary, fontFamily: 'inherit', fontSize: '12px', fontWeight: '500',
    lineHeight: 1, padding: '9px 14px', borderRadius: '999px', cursor: 'pointer',
    whiteSpace: 'nowrap', WebkitTapHighlightColor: 'transparent',
  },
  chipOn: { border: `0.5px solid ${c.blue}`, background: c.blueTint, color: c.blue },

  segWrap: { display: 'flex', alignItems: 'center', gap: '10px', margin: '4px 0 12px' },
  segLabel: {
    fontSize: '11px', fontWeight: '600', letterSpacing: '0.08em',
    textTransform: 'uppercase', color: c.muted, flex: 'none',
  },
  seg: {
    display: 'flex', background: c.input, border: `0.5px solid ${c.chipBorder}`,
    borderRadius: '999px', padding: '2px',
  },
  segBtn: {
    border: 0, background: 'transparent', color: c.secondary, cursor: 'pointer',
    fontFamily: 'inherit', fontSize: '11.5px', fontWeight: '500', lineHeight: 1,
    padding: '7px 12px', borderRadius: '999px', whiteSpace: 'nowrap',
  },
  segBtnOn: { background: c.blueTint, color: c.blue },

  toolbar: { display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '10px' },
  toolbarCount: { fontSize: '12px', color: c.secondary },
  infoBtn: {
    border: 0, background: 'transparent', color: c.link, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    // Draws at 14px, catches at 34.
    width: '34px', height: '34px', margin: '-10px', padding: 0,
  },
  eyeBtn: {
    marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px',
    border: `0.5px solid ${c.chipBorder}`, background: 'transparent', color: c.secondary,
    fontFamily: 'inherit', fontSize: '11.5px', fontWeight: '500', lineHeight: 1,
    padding: '8px 12px', borderRadius: '999px', cursor: 'pointer', whiteSpace: 'nowrap',
  },

  infoCard: {
    background: c.popover, border: `0.5px solid ${c.chipBorder}`, borderRadius: '12px',
    padding: '13px 14px', marginBottom: '12px', cursor: 'pointer',
  },
  infoTitle: { fontSize: '13px', fontWeight: '600', color: c.white, marginBottom: '5px' },
  infoBody: { fontSize: '12px', color: c.secondary, lineHeight: 1.5 },

  list: {
    background: c.card, border: `0.5px solid ${c.hairline}`, borderRadius: '14px',
    overflow: 'hidden',
  },
  empty: { padding: '26px 16px', textAlign: 'center', fontSize: '13px', color: c.muted },
  row: {
    padding: '11px 14px', borderBottom: `0.5px solid ${c.divider}`,
    borderLeft: '2px solid transparent',
  },
  rowLink: { display: 'block', textDecoration: 'none', color: 'inherit' },
  rowCounted: { borderLeft: `2px solid ${c.blue}` },
  // Dims the listing and not the controls. On a held-out row the button you
  // want is the one that puts it back, and greying that out is backwards.
  rowFlagged: { opacity: 0.55 },
  rowActions: { display: 'flex', gap: '7px', marginTop: '9px' },
  act: {
    display: 'inline-flex', alignItems: 'center', gap: '5px',
    border: `0.5px solid ${c.chipBorder}`, background: 'transparent', color: c.muted,
    fontFamily: 'inherit', fontSize: '12px', fontWeight: '600', lineHeight: 1,
    padding: '7px 10px', borderRadius: '7px', cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
  // `border` and not `borderColor`: `act` sets the shorthand, and mixing the
  // two drops the rule.
  actOn: { border: `0.5px solid ${c.blue}`, color: c.white },
  reasons: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '9px' },
  reason: {
    border: `0.5px solid ${c.chipBorder}`, background: 'transparent', color: c.white,
    fontFamily: 'inherit', fontSize: '12px', fontWeight: '600', lineHeight: 1,
    padding: '8px 10px', borderRadius: '7px', cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },
  rowTop: { display: 'flex', alignItems: 'center', gap: '7px' },
  rowPrice: {
    fontSize: '16px', fontWeight: '500', color: c.white,
    fontVariantNumeric: 'tabular-nums',
  },
  rowPriceStruck: { textDecoration: 'line-through' },
  tagIn: {
    fontSize: '9px', fontWeight: '700', letterSpacing: '0.08em',
    color: c.blue, background: c.blueTint, padding: '2px 5px', borderRadius: '4px',
  },
  tagFlag: {
    fontSize: '9px', fontWeight: '700', letterSpacing: '0.08em',
    color: c.red, background: c.redTint, padding: '2px 5px', borderRadius: '4px',
  },
  rowDate: { marginLeft: 'auto', fontSize: '11px', color: c.muted, whiteSpace: 'nowrap' },
  rowTitle: {
    fontSize: '11px', color: c.secondary, marginTop: '3px', lineHeight: 1.4,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },

  footer: { fontSize: '11px', color: c.faint, lineHeight: 1.5, marginTop: '12px' },
});
